/**
 * FeatureSpotlight — 4 variants (image_left_richtext, image_right_richtext, image_top_richtext, floating_card)
 *
 * 1 つのトピックを腰を据えて紹介する中盤コンテンツモジュール。
 * blocks 配列でサブ見出し付きの段落を繰り返し記述できる。
 */
import type { CSSProperties } from 'react';
import type { FeatureSpotlightContent } from '../schemas/feature_spotlight';
import {
  Body,
  Btn,
  Eyebrow,
  H2,
  H3,
  SectionFrame,
} from './primitives';

interface FS {
  content: FeatureSpotlightContent;
}

type ImageFit = 'cover' | 'contain' | 'natural';

/** 画像の表示方法に応じた img スタイル */
function imageStyleFor(fit: ImageFit, aspectRatio: string): CSSProperties {
  if (fit === 'natural') {
    return { width: '100%', height: 'auto', display: 'block' };
  }
  if (fit === 'contain') {
    return {
      width: '100%',
      aspectRatio,
      objectFit: 'contain',
      display: 'block',
      background: 'var(--ls-surface-alt)',
    };
  }
  // cover (default)
  return {
    width: '100%',
    aspectRatio,
    objectFit: 'cover',
    display: 'block',
  };
}

/** リッチテキスト本体: intro + blocks の描画 */
function RichText({ content }: FS) {
  return (
    <>
      {content.intro && (
        <Body style={{ whiteSpace: 'pre-wrap', fontSize: '1rem' }}>{content.intro}</Body>
      )}
      {(content.blocks ?? []).map((b, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {b.heading && (
            <H3 style={{ fontSize: '1.125rem', fontWeight: 700 }}>{b.heading}</H3>
          )}
          {b.body && (
            <Body style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>{b.body}</Body>
          )}
        </div>
      ))}
    </>
  );
}

/** 共通: 画像ブロック */
function ImageBlock({
  src,
  fit,
  aspectRatio = '4 / 3',
  alt = '',
  rounded = true,
}: {
  src: string | null | undefined;
  fit: ImageFit;
  aspectRatio?: string;
  alt?: string;
  rounded?: boolean;
}) {
  if (!src) {
    return (
      <div
        style={{
          width: '100%',
          aspectRatio,
          background: 'var(--ls-surface-alt)',
          borderRadius: rounded ? 'var(--ls-radius-lg)' : 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--ls-text-muted)',
          fontSize: '0.85rem',
        }}
      >
        画像未設定
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      style={{
        ...imageStyleFor(fit, aspectRatio),
        borderRadius: rounded ? 'var(--ls-radius-lg)' : 0,
      }}
    />
  );
}

/** 1. 左に画像、右にリッチテキスト（スクショ例） */
export function FeatureSpotlightImageLeft({ content }: FS) {
  const fit = (content.imageFit ?? 'cover') as ImageFit;
  return (
    <SectionFrame>
      <div className="ls-split-pair">
        <div className="ls-split-media">
          <ImageBlock src={content.image} fit={fit} aspectRatio="4 / 3" alt={content.headline} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && <H2 style={{ whiteSpace: 'pre-wrap' }}>{content.headline}</H2>}
          {content.subheadline && (
            <Body style={{ color: 'var(--ls-text-muted)' }}>{content.subheadline}</Body>
          )}
          <RichText content={content} />
          {content.primaryCta && (
            <div style={{ marginTop: 8 }}>
              <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
                {content.primaryCta}
              </Btn>
            </div>
          )}
        </div>
      </div>
    </SectionFrame>
  );
}

/** 2. 右に画像、左にリッチテキスト（交互配置でリズム作り） */
export function FeatureSpotlightImageRight({ content }: FS) {
  const fit = (content.imageFit ?? 'cover') as ImageFit;
  return (
    <SectionFrame>
      <div className="ls-split-pair">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && <H2 style={{ whiteSpace: 'pre-wrap' }}>{content.headline}</H2>}
          {content.subheadline && (
            <Body style={{ color: 'var(--ls-text-muted)' }}>{content.subheadline}</Body>
          )}
          <RichText content={content} />
          {content.primaryCta && (
            <div style={{ marginTop: 8 }}>
              <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
                {content.primaryCta}
              </Btn>
            </div>
          )}
        </div>
        <div className="ls-split-media">
          <ImageBlock src={content.image} fit={fit} aspectRatio="4 / 3" alt={content.headline} />
        </div>
      </div>
    </SectionFrame>
  );
}

/** 3. 上に画像、下に中央寄せリッチテキスト（モバイル寄り、雑誌風） */
export function FeatureSpotlightImageTop({ content }: FS) {
  const fit = (content.imageFit ?? 'cover') as ImageFit;
  return (
    <SectionFrame>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 32,
          maxWidth: 900,
          margin: '0 auto',
        }}
      >
        <ImageBlock src={content.image} fit={fit} aspectRatio="16 / 9" alt={content.headline} />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            textAlign: 'center',
            alignItems: 'center',
          }}
        >
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && (
            <H2 style={{ whiteSpace: 'pre-wrap', textAlign: 'center' }}>{content.headline}</H2>
          )}
          {content.subheadline && (
            <Body style={{ color: 'var(--ls-text-muted)' }}>{content.subheadline}</Body>
          )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              textAlign: 'left',
              width: '100%',
              maxWidth: 700,
            }}
          >
            <RichText content={content} />
          </div>
          {content.primaryCta && (
            <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
              {content.primaryCta}
            </Btn>
          )}
        </div>
      </div>
    </SectionFrame>
  );
}

/** 4. 背景画像に半透明カードを浮かせる（プレミアム感） */
export function FeatureSpotlightFloatingCard({ content }: FS) {
  const fit = (content.imageFit ?? 'cover') as ImageFit;
  // 背景画像の上に重ねるパターンなので natural の場合は cover 相当で扱う
  const bgFit = fit === 'natural' ? 'cover' : fit;
  return (
    <section
      className="ls-section"
      style={{
        position: 'relative',
        background: content.image
          ? `linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 100%), url(${content.image}) center / ${bgFit} no-repeat`
          : 'var(--ls-surface-alt)',
        minHeight: 540,
      }}
    >
      <div
        className="ls-section-inner"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          minHeight: 540,
        }}
      >
        <div
          style={{
            background: 'var(--ls-bg)',
            padding: 40,
            borderRadius: 'var(--ls-radius-lg)',
            maxWidth: 560,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
          }}
        >
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && (
            <H2 style={{ whiteSpace: 'pre-wrap' }}>{content.headline}</H2>
          )}
          {content.subheadline && (
            <Body style={{ color: 'var(--ls-text-muted)' }}>{content.subheadline}</Body>
          )}
          <RichText content={content} />
          {content.primaryCta && (
            <div style={{ marginTop: 8 }}>
              <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
                {content.primaryCta}
              </Btn>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export const featureSpotlightVariants = {
  image_left_richtext: FeatureSpotlightImageLeft,
  image_right_richtext: FeatureSpotlightImageRight,
  image_top_richtext: FeatureSpotlightImageTop,
  floating_card: FeatureSpotlightFloatingCard,
};
